import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Calendar,
  CalendarOff,
  Shield,
  Users,
  Wallet,
  Cog,
  ChevronRight,
} from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { SETTINGS_CATEGORIES, filterSettingsHubItems } from "../../config/settingsNavigation";
import { hasRole, normalizeRole } from "../../rbac/access";
import SettingsPage from "../pages/SettingsPage";
import {
  getVisitedSettingsPaths,
  markSettingsPathVisited,
} from "./settingsVisited";

const CATEGORY_ICONS = {
  organization: Building2,
  attendance_setup: Calendar,
  leave_setup: CalendarOff,
  payroll_setup: Wallet,
  payroll_tools: Cog,
  statutory_setup: Shield,
  employee_setup: Users,
  security: Shield,
  other: Cog,
};

function isRenderableType(type) {
  if (typeof type === "function") return true;
  return Boolean(type && typeof type === "object" && (type.$$typeof || typeof type.render === "function"));
}

/** Layout tier from link count — keeps card footprints even on the grid. */
function getSettingsCardLayout(itemCount) {
  if (itemCount >= 11) {
    return {
      card: "sw-category-card sw-category-card--expanded",
      items: "sw-card-items sw-card-items--cols-3",
    };
  }
  if (itemCount >= 6) {
    return {
      card: "sw-category-card",
      items: "sw-card-items sw-card-items--cols-2",
    };
  }
  return {
    card: `sw-category-card${itemCount <= 2 ? " sw-category-card--sparse" : ""}`,
    items: "sw-card-items",
  };
}

export default function SettingsHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: me, role: authRole } = useContext(AuthContext);
  const role = useMemo(
    () => normalizeRole(me?.role || me?.user?.role || authRole || "employee"),
    [me, authRole]
  );

  const [visitedPaths, setVisitedPaths] = useState(() => getVisitedSettingsPaths());

  useEffect(() => {
    const sync = () => setVisitedPaths(getVisitedSettingsPaths());
    window.addEventListener("storage", sync);
    window.addEventListener("brixigo-settings-visited", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("brixigo-settings-visited", sync);
    };
  }, []);

  const isVisited = useCallback(
    (path) => {
      const base = String(path || "")
        .split("?")[0]
        .replace(/\/+$/, "") || "/";
      return visitedPaths.some(
        (p) => (String(p).split("?")[0].replace(/\/+$/, "") || "/") === base
      );
    },
    [visitedPaths]
  );

  const openSettingsItem = useCallback(
    (path) => {
      const next = markSettingsPathVisited(path);
      setVisitedPaths(next);
      window.dispatchEvent(new Event("brixigo-settings-visited"));
      navigate(path);
    },
    [navigate]
  );

  const visibleCategories = useMemo(() => {
    return SETTINGS_CATEGORIES.filter((cat) => {
      const catRoles = cat.items.flatMap((it) => it.roles || []);
      if (!catRoles.length) return true;
      return catRoles.some((r) => hasRole(role, [r]));
    });
  }, [role]);

  const totalPages = useMemo(
    () => visibleCategories.reduce((sum, cat) => sum + cat.items.length, 0),
    [visibleCategories]
  );

  if (searchParams.has("tab")) return <SettingsPage />;

  return (
    <div className="sw-home">
      <div className="sw-home-legend" aria-hidden>
        <span className="sw-home-legend__item sw-home-legend__item--fresh">Unvisited</span>
        <span className="sw-home-legend__item sw-home-legend__item--visited">Visited</span>
      </div>

      <div className="sw-cards-grid">
        {visibleCategories.map((cat) => {
          const CatIcon = isRenderableType(CATEGORY_ICONS[cat.id]) ? CATEGORY_ICONS[cat.id] : Cog;
          const visibleItems = filterSettingsHubItems(cat.items).filter((item) => {
            const roles = item.roles || [];
            if (!roles.length) return true;
            return roles.some((r) => hasRole(role, [r]));
          });

          if (!visibleItems.length) return null;

          const layout = getSettingsCardLayout(visibleItems.length);
          const visitedCount = visibleItems.filter((item) => isVisited(item.path)).length;

          return (
            <div
              className={`${layout.card} accent-${cat.accent || "slate"}`}
              key={cat.id}
              data-accent={cat.accent || "slate"}
            >
              <div className="sw-card-header">
                <div className={`sw-card-icon accent-${cat.accent}`}>
                  {isRenderableType(CatIcon) ? <CatIcon size={16} strokeWidth={2} /> : null}
                </div>
                <span className="sw-card-title">{cat.label}</span>
                <span className="sw-card-count" title={`${visitedCount} visited`}>
                  {visibleItems.length}
                </span>
              </div>
              <ul className={layout.items}>
                {visibleItems.map((item) => {
                  const visited = isVisited(item.path);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`sw-card-item ${visited ? "sw-card-item--visited" : "sw-card-item--fresh"}`}
                        onClick={() => openSettingsItem(item.path)}
                      >
                        <span>{item.label}</span>
                        <ChevronRight size={12} strokeWidth={2} aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="sw-pages-badge">
        <strong>{totalPages} pages available</strong>
        <span>You can enable more from All Settings</span>
      </div>
    </div>
  );
}
